import React from 'react'
import { useAppSelector } from '../../../hooks/redux'

import { selectPresence } from '../../../slices/presence'
import useMediaCache from '@SonarCX/hooks/useMediaCache'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'

type useAvatarLoadingProps = {
  user: APIUser
  intersect: any
  group: boolean
}

const useAvatarLoading = ({
  user,
  intersect,
  group
}: useAvatarLoadingProps) => {
  const { presence } = useAppSelector(selectPresence)

  const { getMedia, media } = useMediaCache()
  const { user: currentUser } = useEnvironment()

  const userID = React.useMemo(() => user?.id ?? '', [user])
  const avatarID = React.useMemo(() => user?.avatar_id ?? '', [user])
  const showing = React.useMemo(
    () => (intersect ? intersect.isIntersecting : true),
    [intersect]
  )

  // Look up user presence if not passed down
  const activeStatus = React.useMemo(() => {
    if (!group) {
      return 'offline'
    }

    const status = presence[userID]
    if (!user?.type?.external) {
      // Check if user id is in presence map
      return status
    }

    if (['away', 'online'].includes(status)) {
      return 'online'
    }
  }, [group, presence, userID, user])

  // Get avatar src
  const src = React.useMemo(() => {
    if (!showing) {
      return ''
    }

    if (!avatarID) {
      return ''
    }

    if (!(avatarID in media)) {
      return ''
    }

    return media[avatarID]
  }, [showing, avatarID, media])

  // Fetch avatar when id changes
  React.useEffect(() => {
    if (!avatarID) {
      return
    }

    if (currentUser && currentUser?.id !== userID) {
      return
    }

    getMedia(avatarID, true)
  }, [avatarID, currentUser, userID, getMedia])

  return {
    src,
    elem: null,
    activeStatus
  }
}

export default useAvatarLoading
